import type { RegistrationFieldRecord } from '@/components/event-wizard/registration-builder-utils';
import type { TicketFormFieldSelection } from '@/components/event-wizard/ticket-wizard-utils';

export type ConnectedFormFieldError = {
  index: number;
  formFieldId: string | null;
  message: string;
  code?: string;
  fieldName?: string | null;
};

export function connectableFormFieldId(
  field: Pick<RegistrationFieldRecord, 'id' | 'form_field_id'> | { id?: string; form_field_id?: string | null },
): string | null {
  const mapped = typeof field.form_field_id === 'string' ? field.form_field_id.trim() : '';
  if (mapped) return mapped;
  return null;
}

export function availableFormFieldIds(
  fields: Array<Pick<RegistrationFieldRecord, 'id' | 'form_field_id'>>,
): Set<string> {
  return new Set(
    fields.map((field) => connectableFormFieldId(field)).filter((id): id is string => Boolean(id)),
  );
}

export function reconcileTicketFormFields(
  selected: TicketFormFieldSelection[],
  availableFields: Array<Pick<RegistrationFieldRecord, 'id' | 'form_field_id'>>,
): TicketFormFieldSelection[] {
  const allowed = availableFormFieldIds(availableFields);
  const seen = new Set<string>();
  const next: TicketFormFieldSelection[] = [];

  selected.forEach((row, index) => {
    const id = String(row.form_field_id ?? '').trim();
    if (!id || !allowed.has(id) || seen.has(id)) {
      return;
    }
    seen.add(id);
    next.push({
      form_field_id: id,
      required: Boolean(row.required),
      sort_order: Number.isFinite(row.sort_order) ? row.sort_order : index,
    });
  });

  return next;
}

export function mergeRegistrationFormFieldIds(
  listed: RegistrationFieldRecord[],
  eventFormFields: Array<{ id?: string; slug?: string | null; label?: string }>,
): RegistrationFieldRecord[] {
  if (!Array.isArray(eventFormFields) || eventFormFields.length === 0) {
    return listed;
  }

  const bySlug = new Map<string, string>();
  const byLabel = new Map<string, string>();
  eventFormFields.forEach((field) => {
    const id = String(field.id ?? '').trim();
    if (!id) return;
    if (field.slug) bySlug.set(String(field.slug).trim().toLowerCase(), id);
    if (field.label) byLabel.set(String(field.label).trim().toLowerCase(), id);
  });

  return listed.map((field) => {
    if (field.form_field_id) return field;
    const slug = String(field.slug ?? '').trim().toLowerCase();
    const label = String(field.label ?? '').trim().toLowerCase();
    const mapped = (slug && bySlug.get(slug)) || (label && byLabel.get(label)) || null;
    return mapped ? { ...field, form_field_id: mapped } : field;
  });
}

export function extractValidationMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const extracted = extractValidationMessage(item);
      if (extracted) return extracted;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    const record = value as { message?: unknown };
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message.trim();
    }
  }
  return null;
}

const FORM_FIELD_ERROR_KEY = /^form_fields\.(\d+)\.form_field_id$/;

export function parseConnectedFormFieldErrors(
  fieldErrors: Record<string, string>,
  selected: TicketFormFieldSelection[],
): ConnectedFormFieldError[] {
  const out: ConnectedFormFieldError[] = [];
  for (const [key, message] of Object.entries(fieldErrors)) {
    const match = FORM_FIELD_ERROR_KEY.exec(key);
    if (!match || !message) continue;
    const index = Number(match[1]);
    const formFieldId = selected[index]?.form_field_id ?? null;
    out.push({
      index,
      formFieldId,
      message,
    });
  }
  return out;
}

export function connectedFieldErrorById(
  errors: ConnectedFormFieldError[],
  fieldId: string | null,
): string | undefined {
  if (!fieldId) return undefined;
  return errors.find((error) => error.formFieldId === fieldId)?.message;
}

export function orphanConnectedFieldErrors(
  selected: TicketFormFieldSelection[],
  availableFields: Array<Pick<RegistrationFieldRecord, 'id' | 'form_field_id'>>,
  backendErrors: ConnectedFormFieldError[],
): Array<TicketFormFieldSelection & { error: string; index: number }> {
  const allowed = availableFormFieldIds(availableFields);
  return selected
    .map((row, index) => {
      const backend = backendErrors.find((error) => error.index === index);
      const stale = !allowed.has(row.form_field_id);
      if (!stale && !backend) return null;
      return {
        ...row,
        index,
        error:
          backend?.message ??
          'This registration field is no longer available in this event’s registration form. Please remove it and select a valid field.',
      };
    })
    .filter((row): row is TicketFormFieldSelection & { error: string; index: number } => row !== null)
    .filter((row) => !allowed.has(row.form_field_id));
}

export function hasHighlightableTicketErrors(
  fieldErrors: Record<string, string>,
  highlightableKeys: string[],
): boolean {
  const highlightable = new Set(highlightableKeys);
  return Object.entries(fieldErrors).some(([key, message]) => {
    if (!message) return false;
    return highlightable.has(key) || FORM_FIELD_ERROR_KEY.test(key);
  });
}

export function connectedFieldsSummary(errors: ConnectedFormFieldError[]): string | null {
  if (errors.length === 0) return null;
  return 'Some registration fields need attention. Review the highlighted fields below.';
}
